// HTTP contract between web and server. Every route is under /api, JSON in and out, validated with these schemas.
// Auth: session cookie `pen_session` (HttpOnly, Secure, SameSite=Lax); state-changing requests must carry an Origin
// header equal to the configured origin. All routes except /api/auth/* and GET /api/health require a session.
import { z } from 'zod';
import { Course, Lesson } from './lesson.ts';
import { Attempt, InkEvent, Reply, StuckMark, Turn } from './practice.ts';
import { Review, TargetState } from './learner.ts';
import { TutorIntent } from './learning.ts';

export const ApiError = z.object({
  error: z.enum([
    'unauthenticated', 'forbidden', 'not_found', 'bad_request', 'conflict', 'too_large', 'rate_limited',
    'codex_unlinked', 'codex_auth', 'codex_limit', 'codex_timeout', 'codex_failed', 'server_error',
  ]),
  message: z.string().optional(),
});

// ---- auth (passkeys) ----
// The WebAuthn option/response payloads are @simplewebauthn's JSON types, passed through untouched.
export const Me = z.object({ id: z.string(), handle: z.string(), createdAt: z.string(), codex: z.enum(['linked', 'unlinked', 'pending']) });
export const RegisterOptionsReq = z.object({ invite: z.string().min(8).max(80), handle: z.string().regex(/^[a-z0-9_]{2,24}$/) });
export const LoginOptionsReq = z.object({ handle: z.string().regex(/^[a-z0-9_]{2,24}$/).optional() });
export const RecoverReq = z.object({ handle: z.string().regex(/^[a-z0-9_]{2,24}$/), code: z.string().min(8).max(40) });
/** Returned once after first registration; shown to the user, stored only as hashes. */
export const RecoveryCodes = z.object({ codes: z.array(z.string()).length(10) });
export const Passkey = z.object({ id: z.string(), name: z.string(), createdAt: z.string(), lastUsedAt: z.string().nullable() });

// ---- codex link (each user links their OWN ChatGPT/Codex login) ----
export const CodexLinkStart = z.object({ verificationUrl: z.string().url(), userCode: z.string(), expiresAt: z.string() });

// ---- content ----
export const CourseSummary = z.object({
  course: Course,
  lessons: z.array(z.object({ id: z.string(), title: z.string(), kicker: z.string(), minutes: z.number(),
    progress: z.enum(['new', 'started', 'done']), checkpoints: z.number().int() })),
});
export const LessonResponse = z.object({ lesson: Lesson, progress: z.object({ reachedSection: z.string().nullable(), completedCheckpoints: z.array(z.string()) }) });
export const ProgressReq = z.object({ reachedSection: z.string() });

// ---- practice ----
/**
 * Streaming turns: POST /api/attempts/:id/turns with `Accept: text/event-stream` answers with server-sent events:
 *   event: say    data: {"text": "<the tutor's say so far>"}   (repeated, monotonically growing; may be absent)
 *   event: reply  data: TurnResponse                            (exactly once, last, on success)
 *   event: error  data: ApiError                                (exactly once, last, on failure)
 * Without that Accept header the route answers plain JSON TurnResponse as before.
 */
export const TurnStreamSay = z.object({ text: z.string() });
/** `event: status` on the turn stream: waiting for a free tutor slot ('queued', position 1 = next) or started ('running'). */
export const TurnStreamStatus = z.object({ state: z.enum(['queued', 'running']), position: z.number().int().min(0).optional() });

// ---- voice ----
/** Per-user OpenRouter API key for transcription (stored encrypted server-side; never returned). */
export const VoiceKeyReq = z.object({ apiKey: z.string().min(20).max(300) });
export const VoiceStatus = z.object({ engine: z.enum(['server', 'unconfigured', 'browser']), hasKey: z.boolean(), source: z.enum(['personal', 'server']).nullable().optional(), provider: z.literal('openrouter').optional(), transcribeModel: z.string().optional(), polishModel: z.string().optional() });
/** NDJSON events streamed by POST /api/voice/transcribe (audio body, Content-Type audio/*). */
export const VoiceEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string(), phase: z.enum(['raw', 'polished']) }),
  z.object({ type: z.literal('error'), code: z.enum(['not_configured', 'provider_auth', 'provider', 'too_large', 'no_speech', 'rate_limited']) }),
  z.object({ type: z.literal('done') }),
]);
export const CreateAttemptReq = z.object({ lessonId: z.string(), checkpointId: z.string(), reviewId: z.string().nullable(), w: z.number().positive(), h: z.number().positive(), sittingId: z.string().nullable().optional() });
export const AttemptResponse = z.object({ attempt: Attempt, events: z.array(InkEvent), turns: z.array(Turn), marks: z.array(StuckMark) });
export const AppendEventsReq = z.array(InkEvent).max(2000);
/** ink/process: PNG data URLs at 1 image px = 1 page px of the current page size. */
export const TurnReq = z.object({ ink: z.string().startsWith('data:image/png;base64,'), process: z.string().startsWith('data:image/png;base64,'), message: z.string().max(10_000).nullable(), intent: TutorIntent.optional(), confidence: z.number().int().min(0).max(100).nullable().optional() });
export const TurnResponse = z.object({ reply: Reply, turns: z.array(Turn) });
export const StuckMarkReq = z.object({ t: z.number().int().min(0), note: z.string().max(200) });

// ---- learner ----
export const HomeResponse = z.object({
  due: z.array(Review),
  /** At most three cards, one per course, newest first. */
  continue: z.array(z.object({ lessonId: z.string(), title: z.string(), reachedSection: z.string().nullable(), courseId: z.string(), courseTitle: z.string().nullable(), othersInCourse: z.number().int().min(0) })),
  canNow: z.array(TargetState),
  courses: z.array(z.object({ id: z.string(), title: z.string() })),
});
export const LearnerResponse = z.object({ targets: z.array(TargetState) });

// ---- teach me X ----
export const GenerateReq = z.object({ prompt: z.string().min(4).max(1000) });
export const GenerateJob = z.object({ id: z.string(), status: z.enum(['queued', 'running', 'done', 'failed']), lessonId: z.string().nullable(), error: z.string().nullable(), createdAt: z.string() });

export type ApiError = z.infer<typeof ApiError>;
export type Me = z.infer<typeof Me>;
export type CourseSummary = z.infer<typeof CourseSummary>;
export type LessonResponse = z.infer<typeof LessonResponse>;
export type AttemptResponse = z.infer<typeof AttemptResponse>;
export type TurnResponse = z.infer<typeof TurnResponse>;
export type HomeResponse = z.infer<typeof HomeResponse>;
export type GenerateJob = z.infer<typeof GenerateJob>;
export type CodexLinkStart = z.infer<typeof CodexLinkStart>;
export type Passkey = z.infer<typeof Passkey>;
